import { AttachmentType, L10N, Listing, Localization } from '@smg-real-estate/hg-rets-schema';

import {
  getListingDefaultLocale,
  isDetectedFloorPlan,
  LISTING_LOCALIZATION_LANGUAGES,
} from '@/helpers/ListingData/ListingData';
import { ppaListerPrefixes } from '@/helpers/ListingStats/types';

const isPpaListing = (listing: Listing): boolean =>
  ppaListerPrefixes.some((prefix) => listing.lister?.id?.startsWith(prefix));

const isBasicPpaBundle = (listing: Listing): boolean =>
  listing.valueAddedServices?.bundle?.endsWith('_BASIC') ?? false;

// PKP-6128: Floor plans are not part of the Basic bundle for PPA listings, so detected ones
// must be removed as if they were never uploaded.
function shouldHideDetectedFloorPlans(listing: Listing): boolean {
  return isPpaListing(listing) && isBasicPpaBundle(listing);
}

const withoutDetectedFloorPlans = (l10n: L10N): L10N =>
  l10n.attachments
    ? {
        ...l10n,
        attachments: l10n.attachments.filter((attachment) => !isDetectedFloorPlan(attachment)),
      }
    : l10n;

const getRemovedCountedImages = (listing: Listing, numberOfImages: number): number =>
  (getListingDefaultLocale(listing)?.attachments ?? [])
    .filter((attachment) => attachment.type === AttachmentType.Image)
    .slice(0, numberOfImages)
    .filter(isDetectedFloorPlan).length;

export function getListingWithoutHiddenFloorPlans(listing: Listing): Listing {
  if (!listing.localization || !shouldHideDetectedFloorPlans(listing)) {
    return listing;
  }

  const localization: Localization = { ...listing.localization };
  LISTING_LOCALIZATION_LANGUAGES.forEach((language) => {
    const key = language as 'de' | 'en' | 'fr' | 'it';
    const l10n = localization[key];
    if (l10n) {
      localization[key] = withoutDetectedFloorPlans(l10n);
    }
  });

  const numberOfImages = listing.valueAddedServices?.numberOfImages;

  return {
    ...listing,
    localization,
    ...(numberOfImages !== undefined && {
      valueAddedServices: {
        ...listing.valueAddedServices,
        numberOfImages: numberOfImages - getRemovedCountedImages(listing, numberOfImages),
      },
    }),
  };
}
